import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { History, Plus, Pencil, ArrowRight, MessageSquare, GitBranch, FolderInput, AlignLeft } from "lucide-react";
import { Avatar } from "./BoardCells";
import { Spinner } from "../ui";
import { timeAgo, personOf } from "./social";

const KIND_ICON = { created: Plus, name: Pencil, group: FolderInput, column: ArrowRight, deps: GitBranch, comment: MessageSquare, description: AlignLeft };

export default function ActivityTab({ itemId, schema = [], members = [] }) {
  const { t, lang } = useApp();
  const [log, setLog] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/board-items/${itemId}/activity`);
    setLog(data);
  }, [itemId]);
  useEffect(() => { setLog(null); load(); }, [load]);

  const colOf = (id) => schema.find((c) => c.id === id);
  const display = (col, v) => {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return "—";
    if (!col) return String(v);
    if (["status", "priority", "dropdown"].includes(col.type)) {
      const opt = (col.settings?.options || []).find((o) => o.id === v);
      return opt ? opt.label : String(v);
    }
    if (col.type === "people") {
      const ids = Array.isArray(v) ? v : [v];
      return ids.map((id) => members.find((m) => m.user_id === id)?.name || "?").join(", ");
    }
    if (col.type === "checkbox") return v ? "✓" : "—";
    if (col.type === "timeline" && typeof v === "object") return `${v.start || "?"} → ${v.end || "?"}`;
    return String(v);
  };

  const line = (a) => {
    if (a.kind === "created") return <span>{t("act_created")}</span>;
    if (a.kind === "comment") return <span>{t("act_commented")}{a.text ? <span className="text-text-secondary"> — “{a.text}”</span> : null}</span>;
    if (a.kind === "name") return <span>{t("act_renamed")} <b className="font-semibold">{a.new}</b></span>;
    if (a.kind === "group") return <span>{t("act_moved_group")} <b className="font-semibold">{a.new || "—"}</b></span>;
    if (a.kind === "deps") return <span>{t("act_deps")}</span>;
    if (a.kind === "description") return <span>{t("act_description")}</span>;
    if (a.kind === "column") {
      const col = colOf(a.col_id);
      return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold">{a.col_label || a.col_id}</span>:
          <span className="text-text-secondary line-through">{display(col, a.old)}</span>
          <ArrowRight className="w-3 h-3 text-text-secondary" />
          <span className="font-medium">{display(col, a.new)}</span>
        </span>
      );
    }
    return null;
  };

  return (
    <div data-testid="activity-tab">
      {log === null ? (
        <div className="py-8 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      ) : log.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-secondary"><History className="w-7 h-7 mx-auto mb-2 opacity-40" />{t("act_empty")}</div>
      ) : (
        <ul className="space-y-3">
          {log.map((a) => {
            const Icon = KIND_ICON[a.kind] || History;
            return (
              <li key={a.id} className="flex items-start gap-2.5" data-testid={`activity-${a.id}`}>
                <span className="relative shrink-0 mt-0.5"><Avatar person={personOf(a)} size={26} />
                  <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-background border flex items-center justify-center text-text-secondary"><Icon className="w-2.5 h-2.5" /></span>
                </span>
                <div className="flex-1 min-w-0 text-sm text-text-primary">
                  <span className="font-semibold">{a.actor_name}</span> {line(a)}
                  <div className="text-[11px] text-text-secondary mt-0.5">{timeAgo(a.created_at, lang)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
